import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
	static targets = [
		"freight",
		"total",
		"tokenField",
		"discountRow",
		"discountAmount",
		"couponRow",
		"couponAmount",
		"hint",
		"submit",
		"submitLabel",
		"submitIconPay",
		"submitIconFree",
		"installmentsBox",
		"installmentsSelect",
		"freeShippingHint",
		"freeSection",
		"paymentSection",
		"freeBillingField",
		"paymentFieldset",
	];
	static values = {
		itemsCents: Number,
		shippingCents: Number,
		method: String,
		pixDiscountPercent: Number,
		installmentMinCents: Number,
		installmentOptionTemplate: String,
		couponItemDiscountCents: Number,
		// "Capable" — the coupon WANTS to grant free shipping, pending the
		// service eligibility check below (Cart#coupon_free_shipping_capable?,
		// not the fully-gated #coupon_free_shipping_grant?) — see
		// checkouts/new.html.erb for why the SSR seed can't pre-bake eligibility.
		couponFreeShipping: Boolean,
		// Eligible service keys — StoreCoupon.free_shipping_services, i.e. the
		// configured allowlist narrowed to services that are actually enabled.
		// Free shipping never FORCES a carrier, it only withholds the grant on
		// a pricier one the customer picked instead.
		couponFreeShippingServices: Array,
		shippingService: String,
		// Whether the cart requires shipping at all — used only to decide
		// whether an unquoted shipping_cents (0) means "free" (nothing to
		// ship) or "unknown yet, never speculatively free" (F1, risk review
		// 2026-08-05 — mirrors Cart#zero_total_with_coupon?'s own guard).
		requiresShipping: Boolean,
		// Whether a service is quoted RIGHT NOW, which shippingCents alone can
		// no longer answer: a quote of R$ 0,00 is a real one (a negative
		// shipping_<serviço>_adjust_cents absorbing the whole tariff), so the
		// guard above asks this instead of asking for a positive number. Ruby
		// says the same thing by passing nil rather than 0 — see
		// Cart#zero_total_with_coupon? and checkouts/new.html.erb
		// (store-todo.md §24).
		shippingQuoted: Boolean,
		// StoreCoupon#max_discount_cents, when the coupon has one — absent
		// (hasMaxDiscountCentsValue false) means uncapped.
		maxDiscountCents: Number,
	};

	connect() {
		// Cleared only by a landed frame swap. While true the radios on screen
		// are the previous destination's, so none of them may enable payment or
		// write a token.
		this.stale = false;
		this.render();
	}

	// A destination edit or a frame reload has started, so the quotes currently
	// displayed belong to the destination the buyer just left. Drop the token and
	// keep payment disabled until a reload lands and `turbo:frame-load` ->
	// syncFromChecked reads the fresh frame.
	frameLoading() {
		this.stale = true;
		this.setToken("");
		this.setShippingAvailable(false);
	}

	update(event) {
		// A radio from a frame whose reload has not landed yet carries the old
		// CEP's price and quote token; letting it through is exactly the stale
		// re-enable this guards against.
		if (this.stale) return;

		this.shippingCentsValue = Number(event.target.dataset.priceCents);
		this.shippingServiceValue = event.target.value;
		this.setToken(event.target.dataset.quoteToken);
		this.setShippingAvailable(true);
		this.render();
	}

	syncFromChecked(event) {
		// Only ever fires on a landed `turbo:frame-load`, when the old radios
		// have already been swapped out.
		this.stale = false;

		const checked = event.target.querySelector('input[name="store_order[shipping_service]"]:checked');
		this.shippingCentsValue = checked ? Number(checked.dataset.priceCents) : 0;
		this.shippingServiceValue = checked ? checked.value : "";
		this.setToken(checked ? checked.dataset.quoteToken : "");
		this.setShippingAvailable(Boolean(checked));
		this.render();
	}

	// checkout-carry released the buy button it held during a cart edit.
	resync() {
		if (this.requiresShippingValue) this.setShippingAvailable(!this.stale && this.shippingQuotedValue);
	}

	selectMethod(event) {
		this.methodValue = event.target.value;
		this.render();
	}

	setToken(token) {
		if (this.hasTokenFieldTarget) this.tokenFieldTarget.value = token || "";
	}

	// "Available" and "quoted" are the same fact — a service is checked and
	// carries a price — so the flag is set here, ahead of the submit target's
	// own early return, and every caller keeps passing one boolean.
	setShippingAvailable(available) {
		this.shippingQuotedValue = available;
		if (!this.hasSubmitTarget) return;

		// checkout-carry holds the button while a cart edit is in flight.
		this.submitTarget.disabled = !available || this.submitTarget.dataset.held === "true";
		this.submitTarget.classList.toggle("odo-button__primary", available);
		this.submitTarget.classList.toggle("odo-button__disabled", !available);
	}

	// The coupon comes off the base FIRST — before either the Pix discount or
	// the installment picker — exactly mirroring the server (Cart#coupon_effective_discount_cents,
	// StoreOrder#pix_base_cents/#installment_plan). couponFreeShippingValue
	// ("capable", not yet gated by service) only adds shippingCentsValue when
	// BOTH the coupon wants to grant it AND the currently checked service is
	// eligible — free shipping never forces a carrier (Task D, owner decision
	// 2026-08-05) — so switching carriers moves the coupon row's own amount
	// live, cosmetic only; the server recomputes everything from scratch on
	// submit and is the only real authority.
	//
	// F3 (risk review, 2026-08-05): the SAME live numbers also decide whether
	// this order is zero-total — mirroring Cart#zero_total_with_coupon?(shipping_cents:)'s
	// now-EXACT formula (F1) — so switching carriers can flip the free
	// notice/payment section live, never leaving a stale "Pedido gratuito"
	// beside a nonzero total.
	render() {
		// An EMPTY list means "no service is eligible", matching the server:
		// StoreCoupon#free_shipping_service_eligible? asks `include?`, and
		// `[].include?` is always false. This used to read the raw config, which
		// config/application.rb guarantees is never empty — but the list is now
		// StoreCoupon.free_shipping_services, the configured allowlist narrowed
		// to services actually enabled, and THAT can come back empty (configure
		// only a service that is behind its price-0 kill switch). Treating empty
		// as "everything is eligible" would show a "frete grátis" the server
		// then refuses to grant, and the customer gets charged anyway.
		const freeShippingEligible = !this.shippingServiceValue || this.couponFreeShippingServicesValue.includes(this.shippingServiceValue);
		const grantsFreeShipping = this.couponFreeShippingValue && freeShippingEligible;
		const rawCouponDiscount = this.couponItemDiscountCentsValue + (grantsFreeShipping ? this.shippingCentsValue : 0);
		const cap = this.hasMaxDiscountCentsValue ? this.maxDiscountCentsValue : Infinity;
		const couponDiscount = Math.min(rawCouponDiscount, cap);
		const base = this.itemsCentsValue + this.shippingCentsValue - couponDiscount;
		const isZeroTotal = this.requiresShippingValue && !this.shippingQuotedValue ? false : base === 0;

		if (this.hasFreeSectionTarget || this.hasPaymentSectionTarget) {
			if (isZeroTotal && this.methodValue !== "free") this.methodValue = "free";
			if (!isZeroTotal && this.methodValue === "free") this.methodValue = "pix";
			this.freeSectionTarget.classList.toggle("hidden", !isZeroTotal);
			this.paymentSectionTarget.classList.toggle("hidden", isZeroTotal);
			if (this.hasFreeBillingFieldTarget) this.freeBillingFieldTarget.disabled = !isZeroTotal;
			if (this.hasPaymentFieldsetTarget) this.paymentFieldsetTarget.disabled = isZeroTotal;
			// The submit button's own label must flip right alongside the
			// sections above — otherwise PAC→SEDEX leaves "Concluir pedido" over
			// a payment form, and SEDEX→PAC leaves "Finalizar e pagar" under
			// "Pedido gratuito" (visual gate, 2026-08-06).
			if (this.hasSubmitTarget && this.hasSubmitLabelTarget) {
				this.submitLabelTarget.textContent = this.submitTarget.dataset[isZeroTotal ? "submitLabelFree" : "submitLabelPay"];
			}
			// The icon rides with the label: a free order under the pay-money
			// hand reads as a payment step that isn't there.
			if (this.hasSubmitIconPayTarget) this.submitIconPayTarget.classList.toggle("hidden", isZeroTotal);
			if (this.hasSubmitIconFreeTarget) this.submitIconFreeTarget.classList.toggle("hidden", !isZeroTotal);
		}
		const isPix = this.methodValue === "pix";
		const discount = isPix ? Math.round((base * this.pixDiscountPercentValue) / 100) : 0;

		if (this.hasFreightTarget) this.freightTarget.textContent = this.format(this.shippingCentsValue);
		if (this.hasCouponRowTarget) {
			this.couponRowTarget.classList.toggle("hidden", couponDiscount <= 0);
			this.couponAmountTarget.textContent = `−${this.format(couponDiscount)}`;
		}
		if (this.hasFreeShippingHintTarget) {
			this.freeShippingHintTarget.classList.toggle("hidden", !this.couponFreeShippingValue || freeShippingEligible);
		}
		this.totalTarget.textContent = this.format(isZeroTotal ? 0 : base - discount);

		if (this.hasDiscountRowTarget) {
			this.discountRowTarget.classList.toggle("hidden", !isPix || isZeroTotal);
			if (isPix) this.discountAmountTarget.textContent = `−${this.format(discount)}`;
		}
		// AFTER renderInstallments, never before: it can move the selection
		// itself when a count falls under the minimum, and the hint has to
		// describe the count the customer is left with.
		this.renderInstallments(base, !isPix);
		if (this.hasHintTarget) this.hintTarget.textContent = this.hintFor(isPix);
	}

	// Asaas offers debit only on a single charge — from 2x its payment page
	// renders the credit form alone. The parcelas picker on THIS page is
	// therefore what decides whether debit is still reachable, so the debit
	// sentence tracks it; a fixed "crédito ou débito" would promise a payment
	// method our own selection had already removed. Parcelado gets no sentence
	// in its place: that installments mean credit is not news to a buyer, and
	// spending a line on it would only bury the redirect the hint is for.
	hintFor(isPix) {
		const hints = this.hintTarget.dataset;
		if (isPix) return hints.hintPix;

		const count = this.hasInstallmentsSelectTarget ? Number(this.installmentsSelectTarget.value) : 1;
		return count > 1 ? hints.hintCard : `${hints.hintCard} ${hints.hintCardDebit}`;
	}

	// Cartão shows the parcelas picker; Pix hides AND disables it so a disabled
	// select is never submitted. Every option's label is rewritten from the
	// fresh total in integer CENTS (no floats on currency; the remainder of an
	// uneven split lands on the last parcela at Asaas). An option whose parcela
	// would drop below the minimum is disabled/hidden and the selection falls
	// back to the highest valid count — the server-side validation remains the
	// authority, so a stale selection 422s and never charges wrong.
	renderInstallments(base, isCard) {
		if (!this.hasInstallmentsSelectTarget) return;

		this.installmentsBoxTarget.classList.toggle("hidden", !isCard);
		this.installmentsSelectTarget.disabled = !isCard;
		if (!isCard) return;

		let highestValid = 1;
		for (const option of this.installmentsSelectTarget.options) {
			const count = Number(option.dataset.count);
			const installmentCents = Math.floor(base / count);
			const valid = count === 1 || installmentCents >= this.installmentMinCentsValue;
			option.textContent = this.installmentOptionTemplateValue.replace("%{count}", count).replace("%{value}", this.format(installmentCents));
			option.disabled = !valid;
			option.hidden = !valid;
			if (valid) highestValid = Math.max(highestValid, count);
		}
		const selected = this.installmentsSelectTarget.selectedOptions[0];
		if (!selected || selected.disabled) this.installmentsSelectTarget.value = String(highestValid);
	}

	format(cents) {
		return (cents / 100).toLocaleString(document.documentElement.lang || "pt-BR", { style: "currency", currency: "BRL" });
	}
}
